import React, { createContext, useContext, useState, useEffect } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const initializeProject = async () => {
      try {
        setIsLoading(true);
        const [projectsData, modulesData] = await Promise.all([
          fetchProjects(),
          fetchModules()
        ]);

        const savedProjectId = localStorage.getItem('gyso_current_project_id');
        if (savedProjectId && projectsData.length > 0) {
          const project = projectsData.find(p => p.id === parseInt(savedProjectId));
          if (project) {
            setCurrentProject(project);
          }
        }
      } catch (error) {
        console.error('Project context initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeProject();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects');
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`);
      }
      const data = await response.json();
      setProjects(data);
      return data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  };

  const fetchModules = async () => {
    try {
      const response = await fetch('/api/modules');
      if (!response.ok) {
        throw new Error(`Failed to fetch modules: ${response.status}`);
      }
      const data = await response.json();
      setModules(data);
      return data;
    } catch (error) {
      console.error('Error fetching modules:', error);
      throw error;
    }
  };

  const createProject = async (projectData) => {
    try {
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(projectData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create project: ${response.status}`);
      }

      const newProject = await response.json();
      setProjects(prev => [newProject, ...prev]);
      setCurrentProject(newProject);
      localStorage.setItem('gyso_current_project_id', newProject.id.toString());
      return { success: true, project: newProject };
    } catch (error) {
      console.error('Error creating project:', error);
      return { success: false, error: 'Failed to create project' };
    }
  };

  const selectProject = (project) => {
    setCurrentProject(project);
    localStorage.setItem('gyso_current_project_id', project.id.toString());
  };

  const value = {
    currentProject,
    projects,
    modules,
    isLoading,
    fetchProjects,
    createProject,
    selectProject,
    setCurrentProject,
  };

  return (
    <ErrorBoundary>
      <ProjectContext.Provider value={value}>
        {children}
      </ProjectContext.Provider>
    </ErrorBoundary>
  );
};